import React, { useState, useEffect } from 'react';

import SuggestionDropdown from '../shared/SuggestionDropdown';
import { useDebounce, addItem, focusRef } from '../shared/EventHelpers';
import { getIcon } from '../shared/FormatHelpers';
import { filterExistingItems } from '../HelpMethods';


const ItemsAppender = ({
    iconName,
    items,
    setItems,
    setIsLoading,
    onKeyDown: _onKeyDown,
    getSuggestions,
    isCreationPermitted,
    inputRef,
    autoFocus,
    propName,
    dispatch,
}) => {
    const [userInput, setUserInput] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const maxIndex = suggestions ? (isCreationPermitted ? suggestions.length : suggestions.length - 1) : 0;
    const icon = getIcon(iconName);
    const maxSuggestionsToShow = 50;
    //const { dispatch: exlDispatch } = useContext(ExlDataContext);

    useEffect(() => {
        autoFocus && focusRef(inputRef);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedInput = useDebounce(userInput, 500);
    useEffect(() => {
        if (debouncedInput) {
            const updateSuggestionsAsync = async () => {
                setIsLoading(true);
                if (getSuggestions) setSuggestions(filterExistingItems(await getSuggestions(debouncedInput), items));
                setShowSuggestions(true);
                setIsLoading(false);
            };
            updateSuggestionsAsync();
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput]);

    const createItem = () => {
        if (items?.length && items.some((i) => i.id === null && i.name === userInput)) {
            alert('Du har redan skapat ett objekt med detta namnet.');
            return;
        }
        if (!userInput || !userInput.length) {
            alert('Kan inte skapa objekt utan namn.');
            return;
        }
        const newItem = { id: null, name: userInput, detailedName: userInput, link: null };
        addItem(items, setItems, newItem);
    };

    const addSelectedItem = () => {
        if (isCreationPermitted && activeIndex === maxIndex) {
            createItem();
        } else {
            addItem(items, setItems, suggestions[activeIndex], propName, dispatch);
        }
        setActiveIndex(-1);
        setSuggestions([]);
        setShowSuggestions(false);
        setUserInput('');
    };

    const scrollToIndex = (index) => {
        const divToScrollTo = document.getElementById(`suggestion-${index}`);
        if (divToScrollTo) {
            divToScrollTo.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    };

    const onMouseDown = (event) => {
        if (activeIndex >= 0 && activeIndex <= maxIndex) {
            addSelectedItem();
            event.preventDefault();
        }
    };

    const onKeyDown = (event) => {
        // If empty field, call parent method
        if (!userInput || !userInput.length) {
            _onKeyDown && _onKeyDown(event);
        }

        // Enter adds selected item
        if (event.keyCode === 13) {
            if (activeIndex >= 0 && activeIndex <= maxIndex) {
                addSelectedItem();
            }
            event.preventDefault();
        }

        // Up-arrow moves up in list
        else if (event.keyCode === 38) {
            if (activeIndex > -1) {
                setActiveIndex(activeIndex - 1);
                scrollToIndex(activeIndex - 1);
            }
            event.preventDefault();
        }

        // Down-arrow moves down in list
        else if (event.keyCode === 40) {
            if (activeIndex < maxIndex) {
                setActiveIndex(activeIndex + 1);
                scrollToIndex(activeIndex + 1);
            }
            event.preventDefault();
        }
    };

    const onBlur = () => {
        setShowSuggestions(false);
    };

    const onFocus = () => {
        if (userInput.length) {
            setShowSuggestions(true);
        }
    };

    return (
        <div className='new-item-selector'>
            <div className='d-flex flex-nowrap'>
                {icon}
                <input
                    ref={inputRef}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    onFocus={onFocus}
                />
            </div>
            <SuggestionDropdown
                {...{
                    userInput,
                    suggestions,
                    showSuggestions,
                    activeIndex,
                    setActiveIndex,
                    onMouseDown,
                    isCreationPermitted,
                    maxSuggestionsToShow,
                }}
            />
        </div>
    );
};

export default ItemsAppender;
